import { connect, FormikValues } from 'formik'
import { compact, flatten, get, isArray, sortBy, uniqBy } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import styled, { css } from 'styled-components'

import { FilterSelect } from '@/components/Form'
import {
  StyledWandIcon,
  VariableButton,
  VariableInputContainer
} from '@/pages/SpaceAppBuilder/components/StyledComponents'
import VariableOverlay from '@/pages/SpaceAppBuilder/components/VariableInput/VariableOverlay'
import { FieldTypes, Variable } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import {
  convertVariableTypeToFieldType,
  getAllFlattenedVariables,
  isVariable
} from '@/pages/SpaceAppBuilder/utilities/variableHelpers'
import cn from '@/utilities/cn'

import {
  AutoCompleteAddedOption,
  AutoCompleteAddedStringLabel,
  AutoCompleteVariableLabel,
  AutoCompleteVariableOption
} from './AutoCompleteVariableOption'
import messages from './messages'

const Row = styled.div`
  display: flex;
`

const StyledFilterSelect = styled<any>(FilterSelect).withConfig({
  shouldForwardProp: (prop: any) => !['showValidationMessage'].includes(prop)
})`
  && {
    flex: auto;
    &.form__filter-select {
      margin-bottom: 0;
      margin-top: 0;
      ${({ loading }) =>
        loading &&
        css`
          && {
            .ui.selection.dropdown {
              .dropdown.icon {
                padding: 0;
                top: 30%;
                right: 5px;
                margin-right: 5px;
                &:before {
                  background: none !important;
                }
              }
            }
          }
        `}
      ${({ multiple }) =>
        !multiple &&
        css`
          .field__wrapper .ui.fluid.dropdown {
            height: 100%;
            min-height: 0;
          }
        `}

    .field__error {
        display: ${({ showValidationMessage }) => (showValidationMessage ? 'block' : 'none')};
      }
    }
  }
`

const StyledVariableOverlay = styled(VariableOverlay)`
  && {
    width: calc(100% - 10px);
    flex: 1;
    margin-top: 0;
  }
`

const StyledVariableButton = styled<any>(VariableButton)`
  margin-bottom: auto;
  margin-top: ${({ hasLabel }) => (hasLabel ? '32px' : '0')};
  height: 100%;
  max-height: 45px;
  min-width: 40px;
  display: flex;
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
  svg {
    margin-left: 10px;
  }
`

const StyledFilterSelectContainer = styled.div`
  display: flex;
  width: 100%;
`

enum OptionType {
  String = 'String',
  Variable = 'variable',
  Custom = 'custom'
}

type CustomOption = {
  value: string
  optionType: OptionType
}

interface VariableOption extends Variable {
  value: string
  optionType: OptionType
}

type AutoCompleteOption = Partial<VariableOption> | CustomOption

interface VariableAutoCompleteProps {
  id?: string
  name: string
  className?: string
  label?: string
  variables: Variable[][]
  validVariableTypes?: FieldTypes[]
  variableWithEnumOptionsToInclude?: string
  placeholder: string
  description: string
  required: boolean
  onSelectOption: (option: AutoCompleteOption, name: string) => void
  clearOnSelected?: boolean
  showValidationMessage?: boolean
  showVariableButton?: boolean
  isSearching?: boolean
  renderOption?: (option: AutoCompleteOption) => any
  options?: Array<AutoCompleteOption>
  onSearch?: (value: string) => any
  multiple?: boolean
  allowAdditions?: boolean
  defaultValue?: string
  showOptions?: true
  formik: any
  readOnly?: boolean
}

const VariableAutoComplete = ({
  name,
  className,
  variables,
  validVariableTypes,
  variableWithEnumOptionsToInclude,
  placeholder,
  isSearching = false,
  description,
  descriptionPosition = 'bottom',
  required,
  options,
  onSearch,
  formik,
  renderOption,
  onSelectOption,
  showOptions = true,
  label,
  defaultValue,
  allowAdditions = false,
  showVariableButton = true,
  showValidationMessage = false,
  multiple = false,
  readOnly = false
}: VariableAutoCompleteProps & FormikValues) => {
  const intl = useIntl()

  const [searchTerm, setSearchTerm] = useState('')
  const [showVariablePopOver, setShowVariablePopOver] = useState<string[]>([])

  const isValidVariableType = (variable: Variable) => {
    if (!validVariableTypes) {
      return true
    }

    const type = convertVariableTypeToFieldType(validVariableTypes, variable.type)
    if (type) {
      return validVariableTypes?.includes(type)
    }
    return false
  }

  const setupDefaultOption = () => {
    return typeof defaultValue === 'string'
      ? { value: defaultValue, optionType: OptionType.String }
      : isArray(get(formik.values, name, '')) && allowAdditions
        ? {
            value: get(formik.values, name, ''),
            optionType: OptionType.String
          }
        : {
            ...(defaultValue as unknown as AutoCompleteOption),
            optionType: OptionType.Custom
          }
  }
  const [defaultOption, setDefaultOption] = useState<AutoCompleteOption | undefined>(setupDefaultOption())

  const getAllSearchableOptions = () => {
    const flattenedStepVariables = flatten(variables)
    let searchableOptions: Array<AutoCompleteOption> = getAllFlattenedVariables(flattenedStepVariables)
      .filter(variable => isValidVariableType(variable))
      .map(variable => ({ ...variable, value: variable.name, optionType: OptionType.Variable }))

    //will add list of options from a related variable. This will inspect the enum property of the related variable to display the appropiate options
    if (variableWithEnumOptionsToInclude) {
      const variableWithEnumOptions = searchableOptions.find(
        variable => 'name' in variable && variable.name === variableWithEnumOptionsToInclude
      ) as Variable

      if (variableWithEnumOptions?.enum) {
        searchableOptions = variableWithEnumOptions.enum
          .map((enumOption: string) => ({
            value: enumOption,
            optionType: OptionType.String
          }))
          // @ts-expect-error
          .concat(searchableOptions)
      }
    }

    if (searchTerm.length === 0) {
      if (defaultOption && defaultValue && !isVariable(defaultOption?.value)) {
        //will ensure the already selected default option is always present
        searchableOptions.unshift(defaultOption)
      } else if (defaultOption && isArray(defaultOption?.value)) {
        // will ensure non-variable property keys are present in pick operation
        defaultOption.value
          .filter(option => searchableOptions.every((variable: AutoCompleteOption) => variable.value !== option))
          .map((addedOption: string) =>
            searchableOptions.unshift({
              value: addedOption,
              optionType: defaultOption.optionType || OptionType.Custom
            })
          )
      }
    }

    return uniqBy(searchableOptions.concat(compact(options)), 'value')
  }

  const [autoCompleteOptions, setAutoCompleteOptions] = useState(getAllSearchableOptions)

  useEffect(() => {
    setAutoCompleteOptions(getAllSearchableOptions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options?.length, variableWithEnumOptionsToInclude, defaultOption, variables])

  useEffect(() => {
    if (defaultValue) {
      setDefaultOption(setupDefaultOption())
    } else {
      setDefaultOption(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue])

  useEffect(() => {
    if (
      allowAdditions &&
      defaultOption?.value &&
      !isVariable(defaultOption.value) && //when additions are allowed add the value to the list of options if it doesnt currently exist in options
      !autoCompleteOptions.some(option => option.value === defaultOption.value)
    ) {
      setDefaultOption({
        value: defaultOption.value,
        optionType: OptionType.String
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOption?.value, allowAdditions, options])

  const addVariableFromOverlay = useCallback(
    (variable: AutoCompleteOption) => {
      onSelectOption({ ...variable, type: OptionType.Variable }, name)
    },
    [onSelectOption, name]
  )

  const onVariablePopOverClick = (variablePopoverKey: string) => {
    setShowVariablePopOver((prev: string[]) => {
      if (prev.includes(variablePopoverKey)) {
        return prev.filter(accordionkey => accordionkey !== variablePopoverKey)
      } else {
        return prev.concat(variablePopoverKey)
      }
    })
  }

  const querySearch = (options: any, query: string) => {
    if (query !== searchTerm) {
      setSearchTerm(query)
      onSearch && onSearch(query)
    }
    return sortBy(
      options?.filter((option: any) => {
        if (!multiple && get(formik.values, name, '')?.trim() === option?.value?.trim()) {
          return true
        }

        if (option.optionType === OptionType.Variable) {
          return (
            query?.trim().length > 0 &&
            option?.value?.trim().toLowerCase().replaceAll(/\s/g, '').includes(query.toLowerCase())
          )
        }
        return option
      }),
      'optionType'
    )
  }

  const mapSearchResults = (variables: AutoCompleteOption[]) => {
    return variables.map((option: AutoCompleteOption) => {
      switch (option?.optionType) {
        case OptionType.String: {
          return {
            key: option.value,
            text: <AutoCompleteAddedStringLabel value={option.value as string} />,
            optionType: option.optionType,
            value: option?.value?.toString(),
            content: <AutoCompleteAddedOption value={option.value as string} />
          }
        }
        case OptionType.Variable: {
          return {
            key: option.value,
            text: <AutoCompleteVariableLabel variable={option as Variable} showStepLabel={!multiple} />,
            optionType: option.optionType,
            content: <AutoCompleteVariableOption variable={option as Variable} />,
            value: option.value
          }
        }
        default: {
          return {
            key: option.value,
            text: renderOption ? renderOption(option) : option.value,
            optionType: option.optionType || OptionType.Custom,
            content: renderOption ? renderOption(option) : option.value,
            value: option?.value?.toString()
          }
        }
      }
    })
  }
  return (
    <Row>
      <VariableInputContainer>
        <Row>
          <StyledFilterSelectContainer>
            <StyledFilterSelect
              disabled={readOnly}
              data-testid="variableAutoComplete"
              showValidationMessage={showValidationMessage}
              label={label}
              description={description}
              descriptionPosition={descriptionPosition}
              required={required}
              name={name}
              noResultsMessage={isSearching ? null : intl.formatMessage(messages.noResults)}
              selection={true}
              loading={isSearching}
              onBlur={() => {}}
              onChange={(value: any) => {
                const selectedOption = autoCompleteOptions.find(opt => opt.value?.toString() === value.target.value)
                if (selectedOption) {
                  onSelectOption && onSelectOption(selectedOption, name)
                } else {
                  setDefaultOption({
                    value: value.target.value,
                    optionType: OptionType.String
                  })
                }
                setSearchTerm('')
                setAutoCompleteOptions(getAllSearchableOptions)
              }}
              multiple={multiple}
              allowAdditions={allowAdditions}
              search={querySearch}
              options={showOptions ? mapSearchResults(autoCompleteOptions) : []}
              placeholder={placeholder}
              className={className}
            />
            {showVariableButton && !readOnly && (
              <StyledVariableButton
                className={cn({
                  'mt-[29px]': description
                })}
                aria-label={
                  label
                    ? `${intl.formatMessage(messages.addVariableButton)} ${label}`
                    : intl.formatMessage(messages.addVariableButton)
                }
                hasLabel={!!label}
                onClick={() => {
                  onVariablePopOverClick(name)
                }}
              >
                <StyledWandIcon title={intl.formatMessage(messages.addVariableButton)} />
              </StyledVariableButton>
            )}
          </StyledFilterSelectContainer>
        </Row>
        {showVariablePopOver.includes(name) && showVariableButton && !readOnly && (
          <StyledVariableOverlay
            onSelectVariable={variable => addVariableFromOverlay(variable)}
            onClose={() => {
              setShowVariablePopOver((prev: string[]) => {
                return prev.filter(accordionkey => accordionkey !== name)
              })
            }}
            variables={variables}
            validVariableTypes={validVariableTypes}
          />
        )}
      </VariableInputContainer>
    </Row>
  )
}
export default connect(VariableAutoComplete)
